import { useCallback, useEffect, useState } from 'react';
import { useMe } from './me';
import { useCreditPlanContext } from './use-credit-plan-context';
import { usePersonalSaleEnabled, usePersonalSaleMobileEnabled } from './personal-sale-flag';
import { isNativeArrival } from './upgrade-origin';
import { devToolsUnlocked } from './dev-tools';
import {
  formatPersonalSaleCountdown,
  isPersonalSaleDismissed,
  personalSaleRemainingMs,
  personalSaleWithinWindow,
  setPersonalSaleDismissed,
} from './personal-sale';

/**
 * PERSONAL SALE — the SHOW gate for the banner and the "30% OFF" badge.
 *
 * The offer shows to a **new Free-plan** account for the **24h** after its recipient was created:
 *   FT `symphonyPersonalSale` on · `useCreditPlanContext().isFreePlan` · `Me.recipientRegisteredAt` within 24h.
 * The FT is the ramp lever; the plan and the date are the halves that cannot be forced from Dev Tools —
 * the offer is never shown to a paid account or one past its window, whatever the flag says.
 *
 * The BANNER additionally honours a **session-only dismiss** (its X); the BADGE does not — it has no X
 * and rides the credits card for as long as the offer is live. So dismiss gates the banner alone.
 *
 * Split into two consumer hooks on purpose (perf): the badge only needs a boolean that flips at most once
 * (when the window closes), so it runs a single boundary timer and never re-renders per second; only the
 * banner, which shows a live `HH:MM:SS`, ticks each second — and it stops ticking the moment it hits zero.
 *
 * ── Dev preview ──────────────────────────────────────────────────────────────────────────────────────
 * `?personalSalePreview=1` (honoured only under a dev-tools grant — localhost, or a granted tab) forces
 * the offer ON regardless of plan/flag/date, anchoring a fresh 24h countdown, and persists for the tab so
 * it survives navigation. `?personalSalePreview=0` clears it. This is the ONLY way the banner/badge appear
 * without a real eligible account; a normal launch sets nothing and shows nothing.
 */

const PREVIEW_KEY = 'orion-web:dev:personal-sale-preview';
const PREVIEW_ANCHOR_KEY = 'orion-web:dev:personal-sale-anchor';

type Preview = { on: boolean; anchorMs: number };

/** Client-only. Reads (and, from the query param, writes) the session-scoped preview flag + its anchor. */
function resolvePreview(): Preview {
  if (!devToolsUnlocked() || typeof window === 'undefined') return { on: false, anchorMs: 0 };
  try {
    const param = new URL(window.location.href).searchParams.get('personalSalePreview');
    if (param === '1') window.sessionStorage.setItem(PREVIEW_KEY, '1');
    else if (param === '0') {
      window.sessionStorage.removeItem(PREVIEW_KEY);
      window.sessionStorage.removeItem(PREVIEW_ANCHOR_KEY);
    }
    if (window.sessionStorage.getItem(PREVIEW_KEY) !== '1') return { on: false, anchorMs: 0 };
    let anchor = Number(window.sessionStorage.getItem(PREVIEW_ANCHOR_KEY));
    if (!Number.isFinite(anchor) || anchor <= 0) {
      anchor = Date.now();
      window.sessionStorage.setItem(PREVIEW_ANCHOR_KEY, String(anchor));
    }
    return { on: true, anchorMs: anchor };
  } catch {
    return { on: false, anchorMs: 0 };
  }
}

function usePreview(): Preview {
  // Resolved in an effect (never during SSR/first render) so the flag can't cause a hydration mismatch.
  const [preview, setPreview] = useState<Preview>({ on: false, anchorMs: 0 });
  useEffect(() => {
    setPreview(resolvePreview());
  }, []);
  return preview;
}

interface Eligibility {
  /** Free + flagged + has an anchor date (or preview) — the offer applies, ignoring the window and dismiss. */
  eligible: boolean;
  /** The 24h window's anchor (epoch ms), or null when there is none. */
  anchorMs: number | null;
}

/**
 * Latch of "this browser tab arrived from a native app". `/packages` is the only native→web entry that
 * carries the signal (a native-prefixed origin, or the `native=1` marker), and it STRIPS those params on
 * mount — so it is captured once into `sessionStorage` and read back for the rest of the tab's life,
 * surviving the strip and any in-tab navigation to a surface that carries no such param (home, agents).
 */
const NATIVE_ARRIVAL_KEY = 'orion-web:personal-sale-native-arrival';

/** Pure read (no write): the latch if set, else the live URL signal. False on the server. */
function detectNativeArrival(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.sessionStorage.getItem(NATIVE_ARRIVAL_KEY) === '1') return true;
  } catch {
    // Storage blocked — fall through to the transient URL read.
  }
  return isNativeArrival();
}

/**
 * Whether this browser tab arrived from a native app — resolved SYNCHRONOUSLY (lazy initial state) so
 * the FT reads below can be gated from the very FIRST client render: a native arrival must conduct only
 * the mobile experiment and a web session only the web one, and conducting is an exposure that a delayed
 * (effect-resolved) value would fire against the wrong arm for one render. The flag-dependent output is
 * `false` on the first render regardless (both arms start unfetched), so resolving this synchronously
 * cannot mismatch hydration. Latched in an effect because `/packages` strips its `origin` on mount, so a
 * later navigation to a surface carrying no such param still resolves native.
 */
function useNativeArrival(): boolean {
  const [native] = useState(detectNativeArrival);
  useEffect(() => {
    if (!native) return;
    try {
      window.sessionStorage.setItem(NATIVE_ARRIVAL_KEY, '1');
    } catch {
      // Storage blocked — the synchronous URL read already gave the right answer for this tab.
    }
  }, [native]);
  return native;
}

/**
 * The plan/flag/date half of the gate, shared by the badge and the banner. No time, no dismiss.
 *
 * The FT half is SURFACE-SELECTED: a tab that arrived from a native app reads the native arm
 * (`symphonyPersonalSaleMobile`), everything else reads the web arm (`symphonyPersonalSale`), so the
 * whole feature — banner, badge, /packages pricing, coupon later — follows the FT the user's actual
 * surface was ramped on. The two reads are gated OPPOSITELY (`!native` / `native`) so each session
 * conducts exactly ONE arm: conducting is an exposure, and a native user counted against the web
 * experiment (or vice-versa) pollutes it. `native` is resolved synchronously so neither arm is
 * conducted against the wrong surface for even one render.
 */
function useEligibility(): Eligibility {
  const native = useNativeArrival();
  const webFt = usePersonalSaleEnabled(!native);
  const mobileFt = usePersonalSaleMobileEnabled(native);
  const ft = native ? mobileFt : webFt;
  const { isFreePlan } = useCreditPlanContext();
  const me = useMe();
  const preview = usePreview();

  if (preview.on) return { eligible: true, anchorMs: preview.anchorMs };

  const parsed = me?.recipientRegisteredAt ? Date.parse(me.recipientRegisteredAt) : NaN;
  const anchorMs = Number.isFinite(parsed) ? parsed : null;
  return { eligible: ft && isFreePlan && anchorMs != null, anchorMs };
}

/**
 * The BADGE gate — a boolean that is true while the offer is live. Runs ONE boundary timer that fires
 * exactly when the window closes (not a per-second tick), so a sidebar carrying the badge does not
 * re-render every second.
 */
export function usePersonalSaleActive(): boolean {
  const { eligible, anchorMs } = useEligibility();
  const [, bumpAtExpiry] = useState(0);

  useEffect(() => {
    if (!eligible || anchorMs == null) return;
    const remaining = personalSaleRemainingMs(anchorMs, Date.now());
    if (remaining <= 0) return;
    const id = setTimeout(() => bumpAtExpiry((n) => n + 1), remaining + 50);
    return () => clearTimeout(id);
  }, [eligible, anchorMs]);

  return eligible && anchorMs != null && personalSaleWithinWindow(anchorMs, Date.now());
}

export interface PersonalSaleBannerVisibility {
  /** Whether the banner should render (eligible, in-window, not dismissed this session). */
  visible: boolean;
  /** The 24h window's anchor (epoch ms) — passed to the banner so it runs its OWN per-second countdown. */
  anchorMs: number | null;
  /** Hide the banner for this session only (its X). Does not affect the badge. */
  dismiss: () => void;
}

/**
 * The BANNER'S VISIBILITY gate — held by AppFrame, which reserves the banner's slot height from it. It is
 * deliberately NOT per-second: `visible` flips at most twice (dismiss, and the window closing, via a
 * single boundary timer), so the always-mounted frame does not re-render every second. The live
 * `HH:MM:SS` ticks INSIDE the banner (`usePersonalSaleCountdown`) — the one view that shows it — off the
 * `anchorMs` handed down here, which is stable second-to-second.
 *
 * Owns the session dismiss: the banner is the only surface with an X, and dismiss must not hide the badge.
 */
export function usePersonalSaleBannerVisibility(): PersonalSaleBannerVisibility {
  const { eligible, anchorMs } = useEligibility();
  const [dismissed, setDismissed] = useState(false);
  const [, bumpAtExpiry] = useState(0);

  useEffect(() => {
    setDismissed(isPersonalSaleDismissed());
  }, []);

  const eligibleUndismissed = eligible && anchorMs != null && !dismissed;

  useEffect(() => {
    if (!eligibleUndismissed || anchorMs == null) return;
    const remaining = personalSaleRemainingMs(anchorMs, Date.now());
    if (remaining <= 0) return;
    const id = setTimeout(() => bumpAtExpiry((n) => n + 1), remaining + 50);
    return () => clearTimeout(id);
  }, [eligibleUndismissed, anchorMs]);

  const dismiss = useCallback(() => {
    setPersonalSaleDismissed();
    setDismissed(true);
  }, []);

  const visible = eligibleUndismissed && anchorMs != null && personalSaleWithinWindow(anchorMs, Date.now());
  return { visible, anchorMs, dismiss };
}

/**
 * The banner's live `HH:MM:SS`, ticking each second and STOPPING at zero (perf rule: every repeating
 * clock has a stop on its exit path). Scoped to the banner so only it re-renders per second, never the
 * frame. `anchorMs` comes from `usePersonalSaleBannerVisibility` — a null anchor renders `00:00:00`.
 */
export function usePersonalSaleCountdown(anchorMs: number | null): string {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (anchorMs == null) return;
    setNowMs(Date.now());
    const id = setInterval(() => {
      setNowMs(Date.now());
      if (personalSaleRemainingMs(anchorMs, Date.now()) <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [anchorMs]);
  return formatPersonalSaleCountdown(anchorMs != null ? personalSaleRemainingMs(anchorMs, nowMs) : 0);
}
